// TODO generate typescript queries and mutations to remove all unsafe disables.
// TODO upgrade to aws-amplify v6 and tanstack V5
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable no-console */
import {
  useMutation,
  QueryClient,
  useQuery,
  UseQueryResult,
  UseMutationResult,
  UseInfiniteQueryResult,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { API } from "aws-amplify";
import { Dispatch, SetStateAction } from "react";
import {
  getCompoundFeedList,
  getCustomerBriefBaselinesInterventions,
  listCompoundFeedsV2,
} from "../graphql/queries";
import {
  ModelCompoundFeedConnection,
  ModelCompoundFeedConnectionV2,
  Customer,
  CompoundFeedDeleteResult,
  CompoundFeedDatabase,
} from "../graphql/types";
import {
  deleteCompoundFeed,
  deleteCompoundFeedV2,
  deleteFarm,
  duplicateFarm,
} from "../graphql/mutations";
import { NotificationDialogType } from "../app/sustell_15/components/helpers/NotificationDialog";
import { DialogProps } from "../app/sustell_15/components/CompoundFeeds/CompoundFeeds.types";
import { GraphQLResult } from "@aws-amplify/api-graphql";
import {
  AnimalSystemEnum,
  CoreConnectCompoundFeedNameDatabase,
} from "../app/sustell_15/components/FarmFlow/Baseline/types";
import featureFlags from "../app/sustell_15/utils/featureFlags";
import { coreConnectApiUrl } from "../aws-exports";
import {
  FeedsDashboardOptions,
  FeedsDashboardResponse,
  FeedsDashboardItem,
  CalculatedImpact,
  Component,
  CoreConnectOrganizationDashboardTargetsDto,
} from "./types";

const CUSTOMER_FARMS_KEY = "customerFarms";
const COMPOUND_FEEDS_KEY = "compoundFeeds";
const COMPOUND_FEEDS_KEY_V2 = "compoundFeedsV2";
interface Notification {
  type: NotificationDialogType;
  message: string;
}

type GetCustomerBriefBaselinesInterventions = {
  data: {
    getCustomer: Customer;
  };
};

export const useFetchCustomerFarms = (
  customerId: string,
): UseQueryResult<GetCustomerBriefBaselinesInterventions, Error> =>
  useQuery<GetCustomerBriefBaselinesInterventions, Error>({
    queryKey: [CUSTOMER_FARMS_KEY, customerId],
    queryFn: async () =>
      (await API.graphql({
        query: getCustomerBriefBaselinesInterventions,
        variables: {
          id: customerId,
        },
      })) as GetCustomerBriefBaselinesInterventions,
  });

type GetCompoundFeeds = {
  data: {
    getCompoundFeedList: ModelCompoundFeedConnection;
  };
};

export type GetCompundFeedsV2 = {
  data: {
    listCompoundFeedsV2: ModelCompoundFeedConnectionV2;
    nextToken?: string;
  };
};

export const useFetchCompoundFeeds = (
  customerId: string,
): UseQueryResult<GetCompoundFeeds, Error> => {
  if (!featureFlags.deprecateAFP5) {
    return useQuery<GetCompoundFeeds, Error>({
      queryKey: [COMPOUND_FEEDS_KEY],
      queryFn: async () =>
        (await API.graphql({
          query: getCompoundFeedList,
          variables: {
            customerId,
          },
        })) as GetCompoundFeeds,
    });
  }

  return {
    data: undefined,
    isLoading: false,
  } as UseQueryResult<GetCompoundFeeds, Error>;
};

export const useFetchCompoundFeedsV2 = (
  customerId: string,
): UseQueryResult<GetCompundFeedsV2, Error> =>
  useQuery<GetCompundFeedsV2, Error>({
    queryKey: [COMPOUND_FEEDS_KEY_V2],
    queryFn: async () =>
      (await API.graphql({
        query: listCompoundFeedsV2,
        variables: {
          customerId,
        },
      })) as GetCompundFeedsV2,
  });

export type GetAllCompoundFeedsV2 = {
  listCompoundFeedsV2: ModelCompoundFeedConnectionV2;
};

export const useFetchAllCompoundFeedsV2 = (
  customerId: string,
): UseInfiniteQueryResult<GetAllCompoundFeedsV2, Error> =>
  useInfiniteQuery<GetAllCompoundFeedsV2, Error>({
    queryKey: [COMPOUND_FEEDS_KEY_V2, customerId],
    queryFn: async ({ pageParam }) => {
      const response = (await API.graphql({
        query: listCompoundFeedsV2,
        variables: {
          customerId,
          nextToken: pageParam,
        },
      })) as GraphQLResult<GetAllCompoundFeedsV2>;
      if (!response.data) {
        throw new Error("No data returned");
      }
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.listCompoundFeedsV2.nextToken ?? null;
    },
  });

type FarmIDResult = {
  id: string;
};

type DeleteFarmVariables = { customerId: string; farmId: string };

const deleteFarmOperation = async ({
  customerId,
  farmId,
}: DeleteFarmVariables): Promise<FarmIDResult> => {
  const result = (await API.graphql({
    query: deleteFarm,
    variables: {
      customerId,
      farmId,
    },
  })) as GraphQLResult<{ deleteFarm: FarmIDResult }>;
  return result.data?.deleteFarm as FarmIDResult;
};

export const useDeleteFarm = (
  queryClient: QueryClient,
): UseMutationResult<FarmIDResult, Error, DeleteFarmVariables> =>
  useMutation({
    mutationFn: (variables: DeleteFarmVariables) =>
      deleteFarmOperation(variables),
    onSuccess: async () => {
      await queryClient.invalidateQueries([CUSTOMER_FARMS_KEY]);
    },
    onError: (error) => {
      console.error("Error adding item:", error);
    },
  });

const duplicateFarmOperation = async ({
  customerId,
  farmId,
}: DuplicateFarmVariables): Promise<FarmIDResult> => {
  const result = (await API.graphql({
    query: duplicateFarm,
    variables: {
      customerId,
      farmId,
    },
  })) as GraphQLResult<{ duplicateFarm: FarmIDResult }>;
  return result.data?.duplicateFarm as FarmIDResult;
};

type DuplicateFarmVariables = { customerId: string; farmId: string };

export const useDuplicateFarm = (
  queryClient: QueryClient,
  errorMessage: string,
  setNotification: Dispatch<SetStateAction<null | Notification>>,
): UseMutationResult<FarmIDResult, Error, DuplicateFarmVariables> =>
  useMutation({
    mutationFn: (variables: DuplicateFarmVariables) =>
      duplicateFarmOperation(variables),
    onSuccess: async () => {
      setNotification({
        type: NotificationDialogType.INFO,
        message: "Farm duplicated",
      });
      // The cache invalidation MUST happen after the setNotification call
      // the asynchronous "invalidateQueries" operation create a delay to request icons for the modal
      // it results in a delay to load external components.
      await queryClient.invalidateQueries([CUSTOMER_FARMS_KEY]);
    },
    onError: (error) => {
      console.error("Error adding item:", error);
      let message = errorMessage;
      if (error.message) {
        message = error.message;
      }
      setNotification({
        type: NotificationDialogType.ERROR,
        message,
      });
    },
  });

type DeleteCompoudFeedVariables = { customerId: string; feedId: string };

const deleteCompoundFeedOperation = async ({
  customerId,
  feedId,
}: DeleteCompoudFeedVariables): Promise<CompoundFeedDeleteResult> => {
  const result = (await API.graphql({
    query: deleteCompoundFeed,
    variables: {
      customerId,
      feedId,
    },
  })) as GraphQLResult<{
    deleteCompoundFeed: CompoundFeedDeleteResult;
  }>;

  return result.data?.deleteCompoundFeed as CompoundFeedDeleteResult;
};

export const useDeleteCoumpoundFeed = (
  queryClient: QueryClient,
  ctaClickEvent: (
    linkURL: string,
    linkType: string,
    linkName: string,
    linkSection: string,
    linkLocation: string,
  ) => Promise<void>,
  title: string,
  operationErrorMessage: string,
  successMessage: string,
  errorMessage: string,
  setNotification: Dispatch<SetStateAction<null | DialogProps>>,
): UseMutationResult<
  CompoundFeedDeleteResult,
  Error,
  DeleteCompoudFeedVariables
> =>
  useMutation({
    mutationFn: (variables: DeleteCompoudFeedVariables) =>
      deleteCompoundFeedOperation(variables),
    onSuccess: async (data) => {
      setNotification({
        feedUsageItems: data.usageItems ?? [],
        dialogState: data.status === "OK" ? "normal" : "error",
        dialogTxt: data.status === "OK" ? successMessage : errorMessage,
        dialogTitle: title,
      });
      // The cache invalidation MUST happen after the setNotification call
      // the asynchronous "invalidateQueries" operation create a delay to request icons for the modal
      // it results in a delay to load external components.
      await queryClient.invalidateQueries([COMPOUND_FEEDS_KEY]);
      if (data.id && data.status === "OK") {
        ctaClickEvent(
          window.location.href,
          "link",
          "Delete Feed",
          "My Feeds",
          "My Feeds",
        ).catch((e) => console.log(e));
      }
    },
    onError: (error) => {
      console.error("Error adding item:", error);
      setNotification({
        feedUsageItems: null,
        dialogState: "error",
        dialogTxt: operationErrorMessage,
        dialogTitle: title,
      });
    },
  });

const deleteCompoundFeedV2Operation = async ({
  customerId,
  feedId,
}: DeleteCompoudFeedVariables): Promise<CompoundFeedDeleteResult> => {
  const result = (await API.graphql({
    query: deleteCompoundFeedV2,
    variables: {
      customerId,
      feedId,
    },
  })) as GraphQLResult<{
    deleteCompoundFeedV2: CompoundFeedDeleteResult;
  }>;

  return result.data?.deleteCompoundFeedV2 as CompoundFeedDeleteResult;
};

export const useDeleteCoumpoundFeedV2 = (
  queryClient: QueryClient,
  ctaClickEvent: (
    linkURL: string,
    linkType: string,
    linkName: string,
    linkSection: string,
    linkLocation: string,
  ) => Promise<void>,
  title: string,
  operationErrorMessage: string,
  successMessage: string,
  errorMessage: string,
  setNotification: Dispatch<SetStateAction<null | DialogProps>>,
): UseMutationResult<
  CompoundFeedDeleteResult,
  Error,
  DeleteCompoudFeedVariables
> =>
  useMutation({
    mutationFn: (variables: DeleteCompoudFeedVariables) =>
      deleteCompoundFeedV2Operation(variables),
    onSuccess: async (data) => {
      setNotification({
        feedUsageItems: [],
        dialogState: data.id ? "normal" : "error",
        dialogTxt: data.id ? successMessage : errorMessage,
        dialogTitle: title,
      });
      // The cache invalidation MUST happen after the setNotification call
      // the asynchronous "invalidateQueries" operation create a delay to request icons for the modal
      // it results in a delay to load external components.
      await queryClient.invalidateQueries([COMPOUND_FEEDS_KEY_V2]);
      if (data.id) {
        ctaClickEvent(
          window.location.href,
          "link",
          "Delete Feed",
          "My Feeds",
          "My Feeds",
        ).catch((e) => console.log(e));
      }
    },
    onError: (error) => {
      console.error("Error adding item:", error);
      setNotification({
        feedUsageItems: null,
        dialogState: "error",
        dialogTxt: operationErrorMessage,
        dialogTitle: title,
      });
    },
  });

const getCompoundFeedsByOrganizationId = async ({
  animalSystem,
  database,
  organizationId,
}: {
  animalSystem: string;
  database: string;
  organizationId: string;
}) => {
  const transformApiResponse = (items: any[]) => {
    return items.map((item) => ({
      ...item,
      display_name: item.name,
      value: item.id,
      databaseFoundation: database,
    }));
  };
  const idTokenKey = Object.keys(localStorage).find((key) =>
    key.includes("idToken"),
  );
  const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : "";

  const databaseString = database === "GFLI" ? "GFLI_2_0" : database;
  const url = `${coreConnectApiUrl}/feeds?organizationId=${organizationId}&lciaDatabase=${databaseString}&animalSystem=${animalSystem}`;

  try {
    const response = await fetch(url, {
      headers: {
        Authorization: `${idToken}`,
      },
    });
    const responseBody = await response.json();
    if (response.ok && responseBody.items) {
      return transformApiResponse(responseBody.items);
    }
  } catch (error) {
    return [];
  }
};

const SUSTELL_ORGANIZATION_ID = "eb4a0794-02aa-49a1-8967-c53346d5073b";

const getAllCompoundFeeds = async ({
  animalSystem,
  database,
}: {
  animalSystem: string;
  database: string;
}) => {
  const { id: organizationId } = JSON.parse(
    localStorage.getItem("selectedCustomer") || "{}",
  );

  const [userFeeds, sampleFeeds] = await Promise.all([
    getCompoundFeedsByOrganizationId({
      animalSystem,
      database,
      organizationId,
    }),
    getCompoundFeedsByOrganizationId({
      animalSystem,
      database,
      organizationId: SUSTELL_ORGANIZATION_ID,
    }),
  ]);

  const compoundFeeds = [...(userFeeds ?? []), ...(sampleFeeds ?? [])];

  return compoundFeeds;
};

export const useGetCompoundFeeds = (
  animalSystem: string,
  database: string,
  enabled?: boolean,
): UseQueryResult<CoreConnectCompoundFeedNameDatabase[] | []> => {
  return useQuery({
    queryKey: ["compoundFeeds", animalSystem, database],
    queryFn: () => getAllCompoundFeeds({ animalSystem, database }),
    enabled: enabled !== false,
  });
};

const getAllDatabasesCompoundFeeds = async (animalSystem: string) => {
  const ALL_DATABASES = Object.values(CompoundFeedDatabase);
  const compoundFeeds = await Promise.all(
    ALL_DATABASES.map((database) =>
      getAllCompoundFeeds({ animalSystem, database }),
    ),
  );
  return compoundFeeds.flat();
};

export const useGetAllDatabasesCompoundFeeds = (
  animalSystem: typeof AnimalSystemEnum[keyof typeof AnimalSystemEnum],
  enabled?: boolean,
): UseQueryResult<CoreConnectCompoundFeedNameDatabase[] | []> => {
  return useQuery({
    queryKey: ["allDatabasesCompoundFeeds", animalSystem],
    queryFn: () => getAllDatabasesCompoundFeeds(animalSystem),
    enabled: enabled !== false,
  });
};

const DEFAULT_FEEDS_PER_PAGE = 10;
const DEFAULT_FEEDS_PAGE = 1;
const MAX_FEEDS_PER_PAGE = 20;

const OUTBOUND_TRANSPORT_COMPONENT = "Outbound transports";
const RESOURCES_COMPONENTS = new Set(["Utilities", "Water use"]);

const aggregateComponentsForCoreConnect = (
  components: Component[] = [],
): Component[] => {
  const grouped = {
    ingredients: { impact: 0, impactAbsolute: 0 },
    outbound: { impact: 0, impactAbsolute: 0 },
    resources: { impact: 0, impactAbsolute: 0 },
  };

  components.forEach((component) => {
    if (component.name === OUTBOUND_TRANSPORT_COMPONENT) {
      grouped.outbound.impact += component.impact || 0;
      grouped.outbound.impactAbsolute += component.impactAbsolute || 0;
      return;
    }

    if (RESOURCES_COMPONENTS.has(component.name)) {
      grouped.resources.impact += component.impact || 0;
      grouped.resources.impactAbsolute += component.impactAbsolute || 0;
      return;
    }

    grouped.ingredients.impact += component.impact || 0;
    grouped.ingredients.impactAbsolute += component.impactAbsolute || 0;
  });

  const normalizedComponents: Component[] = [];

  if (grouped.ingredients.impactAbsolute > 0) {
    normalizedComponents.push({
      name: "Ingredients & Inbound transport",
      lookupKey: "Ingredients & Inbound transport",
      label: "Ingredients & Inbound transport",
      impact: grouped.ingredients.impact,
      impactAbsolute: grouped.ingredients.impactAbsolute,
    });
  }

  if (grouped.outbound.impactAbsolute > 0) {
    normalizedComponents.push({
      name: OUTBOUND_TRANSPORT_COMPONENT,
      lookupKey: OUTBOUND_TRANSPORT_COMPONENT,
      label: OUTBOUND_TRANSPORT_COMPONENT,
      impact: grouped.outbound.impact,
      impactAbsolute: grouped.outbound.impactAbsolute,
    });
  }

  if (grouped.resources.impactAbsolute > 0) {
    normalizedComponents.push({
      name: "Utilities",
      lookupKey: "Utilities",
      label: "Utilities",
      impact: grouped.resources.impact,
      impactAbsolute: grouped.resources.impactAbsolute,
    });
  }

  return normalizedComponents.length > 0 ? normalizedComponents : components;
};

const normalizeCoreConnectCalculatedImpact = (
  calculatedImpact: CalculatedImpact | null,
): CalculatedImpact | null => {
  if (!calculatedImpact) {
    return null;
  }

  const normalized = Object.entries(calculatedImpact).reduce(
    (acc, [impactCategory, impactData]) => {
      acc[impactCategory as keyof CalculatedImpact] = {
        ...impactData,
        components: aggregateComponentsForCoreConnect(impactData.components),
      };
      return acc;
    },
    {} as CalculatedImpact,
  );

  return normalized;
};

const resolveCoreConnectContext = () => {
  const idTokenKey = Object.keys(localStorage).find((key) =>
    key.includes("idToken"),
  );
  const idToken = idTokenKey ? localStorage.getItem(idTokenKey) ?? "" : "";
  const selectedCustomer = localStorage.getItem("selectedCustomer");
  if (!idToken || !selectedCustomer) {
    return null;
  }
  const { id: organizationId } = JSON.parse(selectedCustomer) || {};
  if (!organizationId) {
    return null;
  }
  return { idToken, organizationId: String(organizationId) };
};

const buildFeedsDashboardUrl = (
  organizationId: string,
  { perPage, page }: { perPage: number; page: number },
) => {
  const baseUrl = process.env.REACT_APP_ENDPOINT_CORE_CONNECT;
  if (!baseUrl) {
    console.error("Core Connect endpoint is not configured.");
    return null;
  }
  const searchParams = new URLSearchParams({
    organizationId,
    perPage: String(perPage),
    page: String(page),
  });
  return `${baseUrl}/feeds/dashboard?${searchParams.toString()}`;
};

const CORE_CONNECT_CATEGORY_MAP: { [key: string]: string } = {
  CLIMATE_CHANGE: "Climate change",
  CLIMATE_CHANGE_BIOGENIC: "Climate change - Biogenic",
  CLIMATE_CHANGE_FOSSIL: "Climate change - Fossil",
  CLIMATE_CHANGE_LAND_USE_AND_LU_CHANGE:
    "Climate change - Land use and LU change",
  LAND_USE: "Land use",
  RESOURCE_USE_FOSSILS: "Resource use, fossils",
  RESOURCE_USE_MINERALS_AND_METALS: "Resource use, minerals and metals",
  EUTROPHICATION_TERRESTRIAL: "Eutrophication, terrestrial",
  ACIDIFICATION: "Acidification",
  PARTICULATE_MATTER: "Particulate matter",
  IONISING_RADIATION: "Ionising radiation",
  OZONE_DEPLETION: "Ozone depletion",
  HUMAN_TOXICITY_CANCER: "Human toxicity, cancer",
  HUMAN_TOXICITY_NONCANCER: "Human toxicity, non-cancer",
  PHOTOCHEMICAL_OZONE_FORMATION: "Photochemical ozone formation",
  WATER_USE: "Water use",
  EUTROPHICATION_FRESHWATER: "Eutrophication, freshwater",
  EUTROPHICATION_MARINE: "Eutrophication, marine",
  ECOTOXICITY_FRESHWATER: "Ecotoxicity, freshwater",
};

const CORE_CONNECT_UNIT_MAP: { [key: string]: string } = {
  KG_CO2_EQ: "kg CO2 eq",
  PT: "Pt",
  MJ: "MJ",
  KG_SB_EQ: "kg Sb eq",
  MOL_N_EQ: "mol N eq",
  MOL_H_EQ: "mol H+ eq",
  DISEASE_INC: "disease inc.",
  KBQ_U235_EQ: "kBq U-235 eq",
  KG_CFC11_EQ: "kg CFC11 eq",
  CTU_H: "CTUh",
  KG_NMVOC_EQ: "kg NMVOC eq",
  M3_DEPRIV: "m3 depriv.",
  KG_P_EQ: "kg P eq",
  KG_N_EQ: "kg N eq",
  CTU_E: "CTUe",
};

const CORE_CONNECT_SUBCATEGORY_MAP: { [key: string]: string[] } = {
  CLIMATE: ["Air"],
  LAND: ["Land"],
  WATER: ["Water"],
  HUMAN: ["Human"],
};

const transformCoreConnectVersionFootprint = (
  payload: any,
): CalculatedImpact | null => {
  if (!payload?.footprint || !Array.isArray(payload.footprint)) {
    return null;
  }

  const mapped = payload.footprint.reduce((acc: any, item: any) => {
    const categoryName = CORE_CONNECT_CATEGORY_MAP[item?.name] || item?.name;
    if (!categoryName) {
      return acc;
    }

    let ingredientsValue = 0;
    let ingredientsImpact = 0;
    let outboundValue = 0;
    let outboundImpact = 0;
    let resourcesValue = 0;
    let resourcesImpact = 0;

    (item?.drilldown || []).forEach((drilldown: any) => {
      if (drilldown?.name === "DRILLDOWN_INGREDIENTS") {
        ingredientsValue += drilldown?.value || 0;
        ingredientsImpact += drilldown?.percentageTotal || 0;
        return;
      }

      if (drilldown?.name === "DRILLDOWN_TRANSPORT") {
        (drilldown?.contributions || []).forEach((contribution: any) => {
          if (contribution?.name === "INBOUND_TRANSPORTATION") {
            ingredientsValue += contribution?.value || 0;
            ingredientsImpact += contribution?.percentageTotal || 0;
            return;
          }
          if (contribution?.name === "OUTBOUND_TRANSPORTATION") {
            outboundValue += contribution?.value || 0;
            outboundImpact += contribution?.percentageTotal || 0;
          }
        });
        return;
      }

      if (drilldown?.name === "DRILLDOWN_RESOURCES") {
        resourcesValue += drilldown?.value || 0;
        resourcesImpact += drilldown?.percentageTotal || 0;
      }
    });

    const components: Component[] = [];

    if (ingredientsValue > 0) {
      components.push({
        name: "Ingredients & Inbound transport",
        lookupKey: "Ingredients & Inbound transport",
        label: "Ingredients & Inbound transport",
        impact: ingredientsImpact,
        impactAbsolute: ingredientsValue,
      });
    }

    if (outboundValue > 0) {
      components.push({
        name: OUTBOUND_TRANSPORT_COMPONENT,
        lookupKey: OUTBOUND_TRANSPORT_COMPONENT,
        label: OUTBOUND_TRANSPORT_COMPONENT,
        impact: outboundImpact,
        impactAbsolute: outboundValue,
      });
    }

    if (resourcesValue > 0) {
      components.push({
        name: "Utilities",
        lookupKey: "Utilities",
        label: "Utilities",
        impact: resourcesImpact,
        impactAbsolute: resourcesValue,
      });
    }

    acc[categoryName] = {
      label: categoryName,
      impact: {
        value: item?.value || 0,
        unit: CORE_CONNECT_UNIT_MAP[item?.unit] || item?.unit || "",
      },
      components,
      impactCategories: CORE_CONNECT_SUBCATEGORY_MAP[item?.subCategory] || [
        "Other",
      ],
    };

    return acc;
  }, {} as Record<string, unknown>);

  return mapped as unknown as CalculatedImpact;
};

export const fetchFeedVersionFootprintFromCoreConnect = async (
  reference: string,
): Promise<CalculatedImpact | null> => {
  const context = resolveCoreConnectContext();
  if (!context || !reference) {
    return null;
  }

  const baseUrl = process.env.REACT_APP_ENDPOINT_CORE_CONNECT;
  if (!baseUrl) {
    return null;
  }

  const url = `${baseUrl}/feeds/${context.organizationId}/versions/${reference}/footprints`;

  try {
    const response = await fetch(url, {
      headers: {
        Authorization: context.idToken,
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      return null;
    }

    const payload = await response.json();
    return transformCoreConnectVersionFootprint(payload);
  } catch (error) {
    console.error("Error fetching Core Connect version footprint:", error);
    return null;
  }
};

const fetchFeedsDashboard = async (
  options: FeedsDashboardOptions = {},
): Promise<FeedsDashboardResponse | null> => {
  const { perPage = DEFAULT_FEEDS_PER_PAGE, page = DEFAULT_FEEDS_PAGE } =
    options;
  const context = resolveCoreConnectContext();
  if (!context) {
    return null;
  }
  const url = buildFeedsDashboardUrl(context.organizationId, { perPage, page });
  if (!url) {
    return null;
  }
  try {
    const response = await fetch(url, {
      headers: {
        Authorization: context.idToken,
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      console.error("Failed to fetch feeds dashboard:", response.statusText);
      return null;
    }
    const data = (await response.json()) as FeedsDashboardResponse;

    const processedData: FeedsDashboardResponse = {
      ...data,
      items: (data.items || []).map((item) => ({
        ...item,
        calculatedImpact: normalizeCoreConnectCalculatedImpact(
          item.calculatedImpact || null,
        ),
      })),
    };
    return processedData;
  } catch (error) {
    console.error("Error fetching feeds dashboard:", error);
    return null;
  }
};

export const getCoreConnectOrganizationDashboardTargets = (
  referenceId: string,
): Promise<CoreConnectOrganizationDashboardTargetsDto | null> => {
  const context = resolveCoreConnectContext();
  if (!context || !referenceId || !coreConnectApiUrl) {
    return Promise.resolve(null);
  }
  return fetch(
    `${coreConnectApiUrl}/organizations/${encodeURIComponent(
      referenceId,
    )}/dashboard-targets`,
    {
      headers: {
        Authorization: context.idToken,
        "Content-Type": "application/json",
      },
    },
  ).then(async (response) => {
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(
        `Failed to fetch dashboard targets: ${response.statusText}`,
      );
    }
    return response.json();
  });
};

export const putCoreConnectOrganizationDashboardTargets = async (
  referenceId: string,
  dashboardTargets: CoreConnectOrganizationDashboardTargetsDto,
): Promise<void> => {
  const context = resolveCoreConnectContext();
  if (!context || !referenceId || !coreConnectApiUrl) {
    throw new Error("Core Connect organization context is unavailable.");
  }

  const response = await fetch(
    `${coreConnectApiUrl}/organizations/${encodeURIComponent(
      referenceId,
    )}/dashboard-targets`,
    {
      method: "PUT",
      headers: {
        Authorization: context.idToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(dashboardTargets),
    },
  );
  if (!response.ok) {
    throw new Error(
      `Failed to update dashboard targets: ${response.statusText}`,
    );
  }
};

const fetchAllFeedsDashboard = async (): Promise<FeedsDashboardItem[]> => {
  const firstPage = await fetchFeedsDashboard({
    perPage: MAX_FEEDS_PER_PAGE,
    page: 1,
  });
  if (!firstPage) {
    return [];
  }

  const {
    items: firstItems = [],
    total = 0,
    perPage = MAX_FEEDS_PER_PAGE,
  } = firstPage;
  const safePerPage = perPage > 0 ? perPage : MAX_FEEDS_PER_PAGE;
  const totalPages = Math.ceil(total / safePerPage);

  if (totalPages <= 1) {
    return firstItems;
  }

  const remainingPages = Array.from(
    { length: totalPages - 1 },
    (_, index) => index + 2,
  );
  const remainingResponses = await Promise.all(
    remainingPages.map((page) =>
      fetchFeedsDashboard({ perPage: safePerPage, page }),
    ),
  );

  const remainingItems = remainingResponses.flatMap(
    (response) => response?.items ?? [],
  );
  const allItems = [...firstItems, ...remainingItems];
  return allItems;
};

export const useGetFeedsDashboard = (
  options: FeedsDashboardOptions = {},
): UseQueryResult<FeedsDashboardItem[], Error> => {
  const { perPage = DEFAULT_FEEDS_PER_PAGE, page = DEFAULT_FEEDS_PAGE } =
    options;
  return useQuery<FeedsDashboardItem[], Error>(
    ["feedsDashboard", "all", perPage, page],
    () => fetchAllFeedsDashboard(),
  );
};

const mapAnimalSystemForCoreConnect = (animalSystem: string): string => {
  const upper = animalSystem.toUpperCase();
  if (upper === "BEEF") return "CATTLE";
  if (upper === "PIG") return "PIGS";
  return upper;
};

type CoreConnectIngredientOption = {
  value: string;
  text: string;
  isCoreConnect?: boolean;
  origin?: string;
};

const fetchCoreConnectIngredients = async ({
  animalSystem,
  reportingType,
}: {
  animalSystem: string;
  reportingType: string;
}): Promise<CoreConnectIngredientOption[]> => {
  const context = resolveCoreConnectContext();
  if (!context) {
    return [];
  }

  const mappedReportingType =
    reportingType === "FLAG" ? "GHG_FLAG" : "FULL_LCA";
  const mappedSystem = mapAnimalSystemForCoreConnect(animalSystem);
  const url = `${coreConnectApiUrl}/ingredients?organizationId=${context.organizationId}&animalSystem=${mappedSystem}&reportType=${mappedReportingType}`;

  try {
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${context.idToken}`,
      },
    });
    const responseBody = await response.json();
    const items = Array.isArray(responseBody)
      ? responseBody
      : responseBody?.items;

    if (response.ok && Array.isArray(items)) {
      return items.map(
        (item: { id: string; name: string; origin?: string }) => ({
          value: item.id,
          text: item.name,
          isCoreConnect: true,
          origin: item.origin,
        }),
      );
    }
  } catch (error) {}
  return [];
};

export const useGetCoreConnectIngredients = (
  {
    animalSystem,
    reportingType,
  }: { animalSystem: string; reportingType: string },
  enabled?: boolean,
): UseQueryResult<CoreConnectIngredientOption[]> => {
  return useQuery({
    queryKey: ["coreConnectIngredients", animalSystem, reportingType],
    queryFn: () => fetchCoreConnectIngredients({ animalSystem, reportingType }),
    enabled: enabled !== false,
  });
};
